import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import find from 'lodash/find'
import map from 'lodash/map'
import classnames from 'classnames'
import useDragAndDrop from '@Utils/UseDragAndDrop'
import formattedFileSize from '@Utils/FormattedFileSize'
import formattedFileDuration from '@Utils/FormattedFileDuration'
import RemoveFile from '@Images/upload_remove_file.svg'
import FileDrop from '@Images/upload_file_drop.svg'

import {
  IFileToUpload
} from './UploadAndImport'

import {
  IElementForProjectVersion,
  ISubmittingUrl,
} from '@SiteModules/ProjectVersionSettings/types'

import {
  removeFromSubmittingUrls,
  updateDownloadingElements,
} from '@SiteModules/ProjectVersionSettings/actions'

import {
  getHasDownloadingElementsForProjectVersions,
  getDownloadingElementIdsForProjectVersions,
} from '@SiteModules/ProjectVersionSettings/selectors'

import {
  getCurrentWorkspaceId,
} from '@SiteModules/Workspace/selectors'

import './UploadTable.scss'

const UPDATE_ELEMENTS_DELAY = 20000 // ms = 20 sec

interface UploadTableProps {
  readonly elementsForProjectVersions: IElementForProjectVersion[]
  readonly filesToUpload: IFileToUpload[]
  readonly submittingUrls: ISubmittingUrl[]
  readonly showAlert: boolean
  handleFileDrop(filesList: FileList): void
  handleRemoveFile(elementId: string, projectId?: string): void
}

const UploadTable: React.FC<UploadTableProps> = props => {
  const dispatch = useDispatch()

  const {
    elementsForProjectVersions,
    filesToUpload,
    submittingUrls,
    showAlert,
  } = props

  const hasDownloadingElementsForProjectVersions = useSelector(getHasDownloadingElementsForProjectVersions)
  const downloadingElementIdsForProjectVersions = useSelector(getDownloadingElementIdsForProjectVersions)
  const currentWorkspaceId = useSelector(getCurrentWorkspaceId)

  const { dragging, eventHandlers } = useDragAndDrop({ onDrop: props.handleFileDrop })

  useEffect(() => {
    if (!hasDownloadingElementsForProjectVersions) { return }

    const id = window.setInterval(() => {
      dispatch(updateDownloadingElements({
        elementsToUpdateIds: downloadingElementIdsForProjectVersions,
        requestFromDashboard: true,
        workspaceId: currentWorkspaceId
      }))
    }, UPDATE_ELEMENTS_DELAY)

    return () => {
      clearInterval(id)
    }
  }, [hasDownloadingElementsForProjectVersions])

  const renderTableHeader = () => {
    return (
      <tr>
        <th className="UploadTable__name">
          Name
        </th>
        <th className="UploadTable__size">
          Size
        </th>
        <th className="UploadTable__duration">
          Duration
        </th>
        <th className="UploadTable__upload">
          Status
        </th>
        <th className="UploadTable__remove"/>
      </tr>
    )
  }

  const renderUploadProgressBar = (uploadProgress: number) => {
    return (
      <div className="UploadTable__upload-progress-bar">
        <div
          className="UploadTable__upload-progress-bar-fill"
          style={{ width: `${uploadProgress}%` }}
        />
      </div>
    )
  }

  const renderInfiniteLoader = () => {
    return (
      <div className="UploadTable__infinite-loader UploadTable__infinite-loader-loader">
        <div className="UploadTable__infinite-loader-fill" />
      </div>
    )
  }

  const getUrlHostname = (url: string) => {
    try {
      return new URL(url).hostname.replace(/^www\./, '')
    } catch (_error) {
      return 'External URL'
    }
  }

  const renderThumbnail = (thumbnailUrl?: string) => {
    return (
      <div className="UploadTable__thumbnail">
        {thumbnailUrl ? (
          <img
            className="UploadTable__thumbnail-image"
            src={thumbnailUrl}
            alt=""
          />
        ) : (
          <div className="UploadTable__thumbnail-placeholder" />
        )}
      </div>
    )
  }

  const renderMediaTitle = (primaryText: string, thumbnailUrl?: string, secondaryText?: string) => {
    return (
      <div className="UploadTable__media">
        {renderThumbnail(thumbnailUrl)}
        <div className="UploadTable__media-text">
          <div className="UploadTable__name-text">
            {primaryText}
          </div>
          {secondaryText && (
            <div className="UploadTable__name-subtext">
              {secondaryText}
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderStatusWithLoader = (label: string) => {
    return (
      <div className="UploadTable__status-with-loader">
        <span>{label}</span>
        {renderInfiniteLoader()}
      </div>
    )
  }

  const tableRowsElementsForProjectVersions = map(elementsForProjectVersions, (element, index) => {
    const {
      name,
      size,
      duration,
      elementId,
      projectVersionId,
      projectId,
      downloading,
      thumbnailUrl,
    } = element

    const file = find(filesToUpload, fileToUpload => fileToUpload.elementId === elementId)
    const hasCompletedUploadAndProjectVersionCreation = !!projectVersionId && !downloading
    const waiting = !!file && !file.hasStartedUpload
    const uploading = !!file && file.hasStartedUpload && !file.hasCompletedUpload && !hasCompletedUploadAndProjectVersionCreation
    const startingUpload = uploading && file.uploadProgress <= 0
    const reportingUploadProgress = uploading && file.uploadProgress > 0
    const preparing = !hasCompletedUploadAndProjectVersionCreation && (!file || file.hasCompletedUpload)

    const handleRemove = () => props.handleRemoveFile(elementId, projectId)

    return (
      <tr
        data-testid={`project-media-row-${elementId}`}
        className={classnames(
          { 'UploadTable__row-even': index % 2 === 0 },
          { 'UploadTable__row-uneven': index % 2 === 1 }
        )}
        key={elementId}
      >
        <td className="UploadTable__name">
          {renderMediaTitle(name, thumbnailUrl)}
        </td>
        <td className="UploadTable__size">
          {size > 0 ? formattedFileSize(size): '-'}
        </td>
        <td className="UploadTable__duration">
          {downloading ? '-' : typeof(duration) === 'number' && formattedFileDuration(duration)}
        </td>
        <td className="UploadTable__upload">
          {hasCompletedUploadAndProjectVersionCreation && (
            <span className="UploadTable__status UploadTable__status-ready">
              Ready
            </span>
          )}
          {waiting && <span className="UploadTable__status UploadTable__status-waiting">Waiting</span>}
          {startingUpload && renderStatusWithLoader('Starting...')}
          {reportingUploadProgress && (
            <div className="UploadTable__upload-progress">
              <span className="UploadTable__upload-progress-number">
                {`${Math.round(file.uploadProgress)}%`}
              </span>
              {renderUploadProgressBar(file.uploadProgress)}
            </div>
          )}
          {preparing && renderStatusWithLoader('Preparing...')}
        </td>
        <td className="UploadTable__remove">
          <RemoveFile
            data-testid={`project-media-remove-${elementId}`}
            onClick={handleRemove}
          />
        </td>
      </tr>
    )
  })

  const offsetTableRowsSubmittingUrls = elementsForProjectVersions.length

  const tableRowsSubmittingUrls = map(submittingUrls, (submittingUrl, index) => {
    const indexWithOffset = index + offsetTableRowsSubmittingUrls
    const { url, identifier } = submittingUrl

    const handleRemove = () => {
      dispatch(removeFromSubmittingUrls({ submitUrlIdentifier: identifier }))
    }

    return (
      <tr
        className={classnames(
          { 'UploadTable__row-even': indexWithOffset % 2 === 0 },
          { 'UploadTable__row-uneven': indexWithOffset % 2 === 1 }
        )}
        key={identifier}
      >
        <td className="UploadTable__name">
          {renderMediaTitle(url, undefined, getUrlHostname(url))}
        </td>
        <td className="UploadTable__size">
          -
        </td>
        <td className="UploadTable__duration">
          -
        </td>
        <td className="UploadTable__upload">
          {renderStatusWithLoader('Fetching metadata...')}
        </td>
        <td className="UploadTable__remove">
          <RemoveFile
            onClick={handleRemove}
          />
        </td>
      </tr>
    )
  })

  const renderUploadOverlay = () => {
    return (
      <div
        className="UploadTable__overlay"
      >
        <div className="UploadTable__overlay-center">
          <FileDrop />
          <div className="UploadTable__overlay-center-text">
            Drop files to upload them
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={classnames(
        'UploadTable',
        { 'UploadTable__alert': showAlert },
      )}
      {...eventHandlers}
    >
      <table>
        <thead>
          {renderTableHeader()}
        </thead>
        <tbody className="UploadTable__body">
          {tableRowsElementsForProjectVersions}
          {tableRowsSubmittingUrls}
        </tbody>
      </table>
      {dragging && renderUploadOverlay()}
    </div>
  )
}

export default UploadTable
